import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { FORM_FIELD_COMPAT, flattenQuestions, formId, resolveOptionMap, suggestOptionMap, type FormDetail, type FormMapping, type FormQuestion, type FormRoute } from '@kaba/shared';
import { Button, cx, LABEL_BG } from '../../ui/ui';
import { ConditionEditor } from './ConditionEditor';
import { refsBefore, textToTitle, titleToText, type Def } from './model';

const inp = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-9 sm:text-sm';
const PRIO = ['Keine', 'Niedrig', 'Mittel', 'Hoch', 'Dringend'];

function Box({ title, children, hint }: { title: string; children: React.ReactNode; hint?: string }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 sm:p-4" aria-label={title}>
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <p className="text-xs text-[var(--ink-soft)]">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Chips<T extends { id: string; name: string }>({ all, value, onChange, label, color }: { all: T[]; value: string[]; onChange: (v: string[]) => void; label: string; color?: (t: T) => string }) {
  if (!all.length) return <p className="text-xs text-[var(--ink-soft)]">Keine vorhanden.</p>;
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {all.map((x) => (
        <label key={x.id} className="flex min-h-10 items-center gap-1.5 rounded-full border border-[var(--line)] px-3 text-sm has-[:checked]:border-accent has-[:checked]:bg-accent-soft">
          <input type="checkbox" className="sr-only" checked={value.includes(x.id)} onChange={(e) => onChange(e.target.checked ? [...value, x.id] : value.filter((y) => y !== x.id))} />
          {color && <span className={cx('size-2.5 rounded-full', color(x))} aria-hidden />}
          {x.name || 'Ohne Namen'}
        </label>
      ))}
    </div>
  );
}

/** Kartenanlage (Z01–Z03): Standardfall und Zuordnungsregeln von oben nach unten */
export function MappingTab({ def, detail, onChange }: { def: Def; detail: FormDetail; onChange: (d: Def) => void }) {
  const m = def.mapping;
  const set = (p: Partial<FormMapping>) => onChange({ ...def, mapping: { ...m, ...p } });
  const b = detail.board;
  const flat = flattenQuestions(def).filter((f) => !f.section?.repeat);
  const qs = flat.map((f) => f.q).filter((q) => q.type !== 'info');
  const labels = b.labels.map((l) => ({ id: l.id, name: l.name, color: l.color }));

  // Titelvorlage mit Beschriftungen
  const [titleText, setTitleText] = useState(() => titleToText(def, m.title));
  const [titleErr, setTitleErr] = useState<string[]>([]);
  useEffect(() => setTitleText(titleToText(def, m.title)), [m.title]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRoute = (i: number, r: FormRoute | null) => onChange({ ...def, routes: r ? def.routes.map((x, j) => (j === i ? r : x)) : def.routes.filter((_, j) => j !== i) });
  const moveRoute = (i: number, d: number) => {
    const n = [...def.routes];
    [n[i], n[i + d]] = [n[i + d]!, n[i]!];
    onChange({ ...def, routes: n });
  };

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="flex flex-col gap-4">
        <Box title="Ziel" hint="Eine angenommene Einsendung wird genau eine normale Karte in dieser Spalte.">
          <label className="flex flex-col gap-1 text-sm font-medium">
            Zielspalte
            <select className={inp} value={m.columnId ?? ''} onChange={(e) => set({ columnId: e.target.value || null, columnConfirmed: false })}>
              <option value="">Bitte wählen …</option>
              {b.columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>
          {m.columnId && (
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <input type="checkbox" className="size-5" checked={m.columnConfirmed} onChange={(e) => set({ columnConfirmed: e.target.checked })} />
              Ja, neue Einsendungen landen in „{b.columns.find((c) => c.id === m.columnId)?.title}“.
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm font-medium">
            Position in der Spalte
            <select className={inp} value={m.placement} onChange={(e) => set({ placement: e.target.value as 'top' | 'bottom' })}>
              <option value="bottom">Unten anhängen</option>
              <option value="top">Oben einfügen</option>
            </select>
          </label>
        </Box>

        <Box title="Karteninhalt">
          <label className="flex flex-col gap-1 text-sm font-medium">
            Kartentitel
            <input
              className={inp}
              value={titleText}
              maxLength={500}
              onChange={(e) => {
                setTitleText(e.target.value);
                const r = textToTitle(def, e.target.value);
                setTitleErr(r.unknown);
                if (!r.unknown.length) set({ title: r.title });
              }}
            />
            <span className="text-xs font-normal text-[var(--ink-soft)]">Antworten in eckigen Klammern, z. B. [Veranstaltung] · [Anliegen].</span>
          </label>
          {titleErr.length > 0 && <p className="text-xs text-rose-700 dark:text-rose-300">Keine Frage „{titleErr[0]}“ gefunden.</p>}
          <div className="flex flex-wrap gap-1 text-xs">
            {qs
              .filter((q) => q.type !== 'file' && q.type !== 'longtext')
              .map((q) => (
                <button
                  key={q.id}
                  type="button"
                  className="min-h-8 rounded-md bg-black/5 px-2 dark:bg-white/10"
                  onClick={() => {
                    const t = `${titleText}${titleText && !/\s$/.test(titleText) ? ' ' : ''}[${q.label}]`;
                    setTitleText(t);
                    const r = textToTitle(def, t);
                    if (!r.unknown.length) set({ title: r.title });
                  }}
                >
                  + [{q.label}]
                </button>
              ))}
          </div>
          <fieldset className="flex flex-col gap-1 text-sm">
            <legend className="mb-1 font-medium">Beschreibung</legend>
            <label className="flex min-h-10 items-center gap-2">
              <input type="radio" checked={m.description.mode === 'summary' && !m.description.questionIds} onChange={() => set({ description: { mode: 'summary', questionIds: null } })} /> Alle Antworten zusammengestellt
            </label>
            <label className="flex min-h-10 items-center gap-2">
              <input type="radio" checked={m.description.mode === 'summary' && !!m.description.questionIds} onChange={() => set({ description: { mode: 'summary', questionIds: qs.map((q) => q.id) } })} /> Ausgewählte Antworten
            </label>
            {m.description.mode === 'summary' && m.description.questionIds && (
              <div className="ml-6 flex flex-col">
                {flattenQuestions(def)
                  .filter((f) => f.q.type !== 'info')
                  .map((f) => (f.section?.repeat ? { id: f.section.id, label: f.section.title } : { id: f.q.id, label: f.q.label }))
                  .filter((x, i, a) => a.findIndex((y) => y.id === x.id) === i)
                  .map((x) => (
                    <label key={x.id} className="flex min-h-9 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={m.description.questionIds!.includes(x.id)}
                        onChange={(e) => set({ description: { mode: 'summary', questionIds: e.target.checked ? [...m.description.questionIds!, x.id] : m.description.questionIds!.filter((y) => y !== x.id) } })}
                      />
                      {x.label}
                    </label>
                  ))}
              </div>
            )}
            <label className="flex min-h-10 items-center gap-2">
              <input type="radio" checked={m.description.mode === 'none'} onChange={() => set({ description: { mode: 'none' } })} /> Keine (Antworten stehen in der Originaleinsendung)
            </label>
          </fieldset>
          {b.cardTemplates.length > 0 && (
            <label className="flex flex-col gap-1 text-sm font-medium">
              Kartenvorlage
              <select className={inp} value={m.cardTemplateId ?? ''} onChange={(e) => set({ cardTemplateId: e.target.value || null })}>
                <option value="">Keine</option>
                {b.cardTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <span className="text-xs font-normal text-[var(--ink-soft)]">Checkliste, Labels und Frist der Vorlage werden beim Veröffentlichen übernommen.</span>
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm font-medium">
            Kontaktangabe
            <select className={inp} value={m.contactQ ?? ''} onChange={(e) => set({ contactQ: e.target.value || null })}>
              <option value="">Keine</option>
              {qs
                .filter((q) => q.type === 'email' || q.type === 'text' || q.type === 'phone')
                .map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.label}
                  </option>
                ))}
            </select>
            <span className="text-xs font-normal text-[var(--ink-soft)]">Steht in der Einsendung als Kontakt. Ungeprüft – nie als Absender oder Konto behandelt.</span>
          </label>
        </Box>

        <Box title="Eigene Felder" hint="Antworten in passende Boardfelder schreiben. Neue Felder legst du im Board unter „Felder“ an.">
          {m.fields.map((f, i) => {
            const field = b.fields.find((x) => x.id === f.fieldId);
            const q = qs.find((x) => x.id === f.q);
            const choice = !!field && !!q && (field.type === 'select' || field.type === 'multiselect') && (q.type === 'choice' || q.type === 'multichoice');
            const setRow = (p: Partial<FormMapping['fields'][number]>) => set({ fields: m.fields.map((x, j) => (j === i ? { ...x, ...p } : x)) });
            // Neue Paarung: Optionen per Namensvorschlag vorbelegen – danach gelten nur die gespeicherten IDs (F03)
            const suggest = (fid: string, qid: string) => {
              const fl = b.fields.find((x) => x.id === fid);
              const qq = qs.find((x) => x.id === qid);
              return fl && qq?.options ? suggestOptionMap(qq.options, fl.options) : null;
            };
            return (
              <div key={i} className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <select className={cx(inp, 'w-auto flex-1')} aria-label="Boardfeld" value={f.fieldId} onChange={(e) => setRow({ fieldId: e.target.value, options: suggest(e.target.value, f.q) })}>
                    {!field && <option value={f.fieldId}>Gelöschtes Feld</option>}
                    {b.fields.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm">←</span>
                  <select className={cx(inp, 'w-auto flex-1')} aria-label="Frage" value={f.q} onChange={(e) => setRow({ q: e.target.value, options: suggest(f.fieldId, e.target.value) })}>
                    {qs
                      .filter((q) => !field || FORM_FIELD_COMPAT[q.type].includes(field.type) || q.id === f.q)
                      .map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.label}
                        </option>
                      ))}
                  </select>
                  <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:text-rose-600" aria-label="Zuordnung entfernen" onClick={() => set({ fields: m.fields.filter((_, j) => j !== i) })}>
                    <Trash2 size={15} />
                  </button>
                </div>
                {choice && <OptionMapEditor q={q!} fieldOptions={field!.options} value={f.options} onChange={(options) => setRow({ options })} />}
              </div>
            );
          })}
          {b.fields.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={() => {
                const field = b.fields.find((x) => !m.fields.some((f) => f.fieldId === x.id)) ?? b.fields[0]!;
                const q = qs.find((x) => FORM_FIELD_COMPAT[x.type].includes(field.type)) ?? qs[0];
                if (q) set({ fields: [...m.fields, { fieldId: field.id, q: q.id, options: q.options ? suggestOptionMap(q.options, field.options) : null }] });
              }}
            >
              <Plus size={14} /> Feld zuordnen
            </Button>
          )}
          {!b.fields.length && <p className="text-xs text-[var(--ink-soft)]">Das Board hat keine eigenen Felder.</p>}
        </Box>

        <Box title="Termine" hint="Ein Wunschdatum ist keine Frist. Ordne Fristen bewusst zu.">
          <label className="flex flex-col gap-1 text-sm font-medium">
            Beginn
            <select className={inp} value={m.start?.q ?? ''} onChange={(e) => set({ start: e.target.value ? { q: e.target.value } : null })}>
              <option value="">Nicht setzen</option>
              {qs
                .filter((q) => q.type === 'date' || q.type === 'datetime')
                .map((q) => (
                  <option key={q.id} value={q.id}>
                    Antwort „{q.label}“
                  </option>
                ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            Fälligkeit
            <select
              className={inp}
              value={!m.due ? '' : 'q' in m.due ? `q:${m.due.q}` : 'days'}
              onChange={(e) => {
                const v = e.target.value;
                set({ due: !v ? null : v === 'days' ? { days: 7 } : { q: v.slice(2) } });
              }}
            >
              <option value="">Nicht setzen</option>
              <option value="days">Feste Frist nach Eingang</option>
              {qs
                .filter((q) => q.type === 'date' || q.type === 'datetime')
                .map((q) => (
                  <option key={q.id} value={`q:${q.id}`}>
                    Antwort „{q.label}“
                  </option>
                ))}
            </select>
          </label>
          {m.due && 'days' in m.due && (
            <label className="flex items-center gap-2 text-sm">
              <input className={cx(inp, 'w-20')} inputMode="numeric" value={m.due.days} onChange={(e) => set({ due: { days: Math.max(0, Math.min(365, Number(e.target.value) || 0)) } })} /> Tage nach Eingang
            </label>
          )}
        </Box>
      </div>

      <div className="flex flex-col gap-4">
        <Box title="Vorgaben für jede Karte">
          <div className="flex flex-col gap-1 text-sm font-medium">
            Labels
            <Chips all={labels} value={m.labelIds} onChange={(v) => set({ labelIds: v })} label="Labels" color={(l) => LABEL_BG[l.color]} />
          </div>
          <label className="flex flex-col gap-1 text-sm font-medium">
            Priorität
            <select className={inp} value={m.priority} onChange={(e) => set({ priority: Number(e.target.value) })}>
              {PRIO.map((p, i) => (
                <option key={i} value={i}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-col gap-1 text-sm font-medium">
            Zuständig
            <Chips all={b.assignable} value={m.assigneeIds} onChange={(v) => set({ assigneeIds: v })} label="Zuständige" />
            <span className="text-xs font-normal text-[var(--ink-soft)]">Nur Personen, die das Board bearbeiten dürfen. Verliert jemand den Zugang, bleibt die Karte unzugewiesen und Besitzer erhalten einen Hinweis.</span>
          </div>
          {qs.some((q) => q.type === 'file') && (
            <label className="flex flex-col gap-1 text-sm font-medium">
              Titelbild aus
              <select className={inp} value={m.coverQ ?? ''} onChange={(e) => set({ coverQ: e.target.value || null })}>
                <option value="">Kein Titelbild</option>
                {qs
                  .filter((q) => q.type === 'file')
                  .map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.label} (erstes Bild)
                    </option>
                  ))}
              </select>
            </label>
          )}
        </Box>

        <Box title="Zuordnungsregeln" hint="Von oben nach unten geprüft – die erste passende Regel gewinnt. Passt keine, gilt der Standardfall oben.">
          {def.routes.map((r, i) => (
            <div key={r.id} className="flex flex-col gap-2 rounded-lg border border-[var(--line)] p-2.5">
              <div className="flex items-center gap-1">
                <span className="rounded bg-black/5 px-1.5 text-xs tabular-nums dark:bg-white/10">{i + 1}</span>
                <input className={cx(inp, 'flex-1')} aria-label={`Name der Regel ${i + 1}`} value={r.name} maxLength={80} onChange={(e) => setRoute(i, { ...r, name: e.target.value })} />
                <button type="button" className="inline-flex size-10 items-center justify-center rounded-md disabled:opacity-30" aria-label={`Regel „${r.name}“ nach oben`} disabled={i === 0} onClick={() => moveRoute(i, -1)}>
                  <ArrowUp size={14} />
                </button>
                <button type="button" className="inline-flex size-10 items-center justify-center rounded-md disabled:opacity-30" aria-label={`Regel „${r.name}“ nach unten`} disabled={i === def.routes.length - 1} onClick={() => moveRoute(i, 1)}>
                  <ArrowDown size={14} />
                </button>
                <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:text-rose-600" aria-label={`Regel „${r.name}“ löschen`} onClick={() => setRoute(i, null)}>
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-xs font-semibold">Wenn …</p>
              <ConditionEditor def={def} refs={refsBefore(def, 'end')} value={r.when} onChange={(c) => setRoute(i, { ...r, when: c ?? { mode: 'all', items: [] } })} label="Bedingung" />
              <p className="text-xs font-semibold">… dann</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  Spalte
                  <select className={inp} value={r.columnId ?? ''} onChange={(e) => setRoute(i, { ...r, columnId: e.target.value || null })}>
                    <option value="">wie Standardfall</option>
                    {b.columns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Priorität
                  <select className={inp} value={r.priority ?? ''} onChange={(e) => setRoute(i, { ...r, priority: e.target.value === '' ? null : Number(e.target.value) })}>
                    <option value="">wie Standardfall</option>
                    {PRIO.map((p, j) => (
                      <option key={j} value={j}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Interne Frist
                  <span className="flex items-center gap-1.5">
                    <input className={cx(inp, 'w-20')} inputMode="numeric" placeholder="–" value={r.dueDays ?? ''} onChange={(e) => setRoute(i, { ...r, dueDays: e.target.value === '' ? null : Math.max(0, Math.min(365, Number(e.target.value) || 0)) })} /> Tage
                  </span>
                </label>
              </div>
              <SetPicker label="Labels" all={labels} color={(l) => LABEL_BG[l.color]} value={r.labels ?? null} onChange={(v) => setRoute(i, { ...r, labels: v })} />
              <SetPicker label="Zuständige" all={b.assignable} value={r.assignees ?? null} onChange={(v) => setRoute(i, { ...r, assignees: v })} />
            </div>
          ))}
          <Button type="button" size="sm" variant="ghost" className="self-start" disabled={def.routes.length >= 30} onClick={() => onChange({ ...def, routes: [...def.routes, { id: formId('r'), name: `Regel ${def.routes.length + 1}`, when: { mode: 'all', items: [] } }] })}>
            <Plus size={14} /> Regel
          </Button>
        </Box>
      </div>
    </div>
  );
}

/** Mengenfelder: „ergänzen“ oder „ersetzen“ ausdrücklich (Z02) */
function SetPicker<T extends { id: string; name: string }>({ label, all, value, onChange, color }: { label: string; all: T[]; value: { mode: 'add' | 'replace'; ids: string[] } | null; onChange: (v: { mode: 'add' | 'replace'; ids: string[] } | null) => void; color?: (t: T) => string }) {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="flex flex-wrap items-center gap-2">
        {label}
        <select className="min-h-9 rounded-md border border-[var(--line)] bg-[var(--panel)] px-2 text-sm" aria-label={`${label}: Wirkung`} value={value ? value.mode : ''} onChange={(e) => onChange(e.target.value ? { mode: e.target.value as 'add', ids: value?.ids ?? [] } : null)}>
          <option value="">nicht ändern</option>
          <option value="add">ergänzen</option>
          <option value="replace">ersetzen</option>
        </select>
      </span>
      {value && <Chips all={all} value={value.ids} onChange={(ids) => onChange({ ...value, ids })} label={label} color={color} />}
    </div>
  );
}

/** Auswahl → Option des Boardfelds, fest über IDs; Umbenennen im Board ändert nichts (F03) */
function OptionMapEditor({ q, fieldOptions, value, onChange }: { q: FormQuestion; fieldOptions: { id: string; label: string }[]; value: Record<string, string> | null | undefined; onChange: (v: Record<string, string>) => void }) {
  const opts = q.options ?? [];
  const { map, missing } = resolveOptionMap(opts, fieldOptions, value);
  const current = Object.fromEntries(map);
  return (
    <div className="ml-2 flex flex-col gap-1 border-l-2 border-[var(--line)] pl-3" role="group" aria-label={`Auswahl „${q.label}“ zuordnen`}>
      {!value && <p className="text-xs text-[var(--ink-soft)]">Vorschlag nach gleichem Namen – wird beim Veröffentlichen fest übernommen.</p>}
      {opts.map((o) => (
        <label key={o.id} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="min-w-24 flex-1 truncate">{o.label}</span>
          <span aria-hidden>→</span>
          <select
            className={cx(inp, 'w-auto flex-1', !map.has(o.id) && 'border-rose-400')}
            aria-label={`Option für „${o.label}“`}
            value={map.get(o.id) ?? ''}
            onChange={(e) => {
              const next = { ...current };
              if (e.target.value) next[o.id] = e.target.value;
              else delete next[o.id];
              onChange(next);
            }}
          >
            <option value="">– nicht zugeordnet –</option>
            {fieldOptions.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        </label>
      ))}
      {missing.length > 0 && <p className="text-xs text-rose-700 dark:text-rose-300">Ohne Zuordnung kann das Formular nicht veröffentlicht werden.</p>}
    </div>
  );
}
